import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, 
  FileText, 
  BookOpen, 
  Brain, 
  ListChecks,
  HelpCircle,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  RotateCcw,
  Trophy,
  Clock,
  Target,
  Loader2,
  GraduationCap
} from 'lucide-react';
import ExamMode from '../components/ExamMode';
import AddToCourseModal from '../components/AddToCourseModal';

const StudyDashboard = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [document, setDocument] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [studyMaterials, setStudyMaterials] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  
  // Flashcard state
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  
  // Course modal state
  const [showCourseModal, setShowCourseModal] = useState(false);

  // Quiz state
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizSet, setQuizSet] = useState(0);

  useEffect(() => {
    fetchData();
  }, [documentId]);

  const fetchData = async () => {
    try {
      // Fetch document details
      const docRes = await axios.get(`${API}/documents/${documentId}`);
      // API returns nested document object
      const docData = docRes.data.document || docRes.data;
      setDocument(docData);
      
      // Fetch purchase info
      const purchasesRes = await axios.get(`${API}/purchases`);
      const userPurchase = purchasesRes.data.find(p => p.document_id === documentId);
      setPurchase(userPurchase);
      
      // Check access - use is_owner from API response or compare seller_id
      const isOwner = docRes.data.is_owner || docData.seller_id === user?.id;
      if (!userPurchase && !isOwner) {
        toast.error('You must purchase this document to access study materials');
        navigate(`/document/${documentId}`);
        return;
      }
      
      // Fetch study materials
      await fetchStudyMaterials();
      
      // Fetch progress
      const progressRes = await axios.get(`${API}/study-progress/${documentId}`);
      setProgress(progressRes.data);
      
      // Load file for viewing if available
      if (docRes.data.has_file || docData.has_file) {
        loadFile();
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('You must purchase this document to access study materials');
        navigate(`/document/${documentId}`);
      } else if (error.response?.status === 402) {
        toast.error('Study materials have not been generated yet');
        navigate('/study-builder');
      } else {
        toast.error('Failed to load study materials');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStudyMaterials = async () => {
    try {
      const res = await axios.get(`${API}/study-materials/${documentId}`);
      if (res.data.status === 'generating') {
        setGenerating(true);
        // Poll for completion
        setTimeout(fetchStudyMaterials, 3000);
      } else if (res.data.status === 'completed') {
        setStudyMaterials(res.data);
        setGenerating(false);
      } else {
        setStudyMaterials(res.data);
        setGenerating(false);
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching study materials:', error);
      }
    }
  };

  const loadFile = async () => {
    try {
      const isDocx = studyMaterials?.file_type?.includes('wordprocessingml') ||
                     studyMaterials?.file_name?.endsWith('.docx');
      if (isDocx) {
        const response = await axios.get(`${API}/documents/${documentId}/file`);
        const html = typeof response.data === 'string' ? response.data : '';
        setDocxHtml(html);
      } else {
        const response = await axios.get(`${API}/documents/${documentId}/file`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = URL.createObjectURL(blob);
        setFileUrl(url);
      }
    } catch (error) {
      console.error('Error loading file:', error);
    }
  };

  const handleMasterFlashcard = async (index) => {
    try {
      const isMastered = progress?.flashcards_mastered?.includes(index);
      if (isMastered) {
        await axios.post(`${API}/study-progress/${documentId}/flashcard/${index}/unmaster`);
        setProgress(prev => ({
          ...prev,
          flashcards_mastered: prev.flashcards_mastered.filter(i => i !== index)
        }));
      } else {
        await axios.post(`${API}/study-progress/${documentId}/flashcard/${index}/master`);
        setProgress(prev => ({
          ...prev,
          flashcards_mastered: [...(prev.flashcards_mastered || []), index]
        }));
      }
    } catch (error) {
      toast.error('Failed to update flashcard status');
    }
  };

  // Get quiz questions for current set
  const getQuizQuestionsForSet = () => {
    if (!studyMaterials?.quiz_questions) return [];
    const totalSets = studyMaterials.quiz_sets || 1;
    if (totalSets <= 1) return studyMaterials.quiz_questions;
    // Try "set" field first
    const setQuestions = studyMaterials.quiz_questions.filter(q => q.set === quizSet);
    if (setQuestions.length > 0) return setQuestions;
    // Fall back to positional slicing (10 per set)
    return studyMaterials.quiz_questions.slice(quizSet * 10, quizSet * 10 + 10);
  };

  const handleSubmitQuiz = async () => {
    const questions = getQuizQuestionsForSet();
    if (selectedAnswers.length !== questions.length) {
      toast.error('Please answer all questions');
      return;
    }
    
    setSubmittingQuiz(true);
    try {
      const res = await axios.post(`${API}/study-progress/${documentId}/quiz-attempt`, {
        answers: selectedAnswers,
        quiz_set: quizSet
      });
      setQuizResults(res.data);
      setProgress(prev => ({
        ...prev,
        quiz_attempts: res.data.attempts,
        best_quiz_score: res.data.best_score
      }));
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const resetQuiz = () => {
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswers([]);
    setQuizResults(null);
  };

  const nextQuizSet = () => {
    const totalSets = studyMaterials?.quiz_sets || 1;
    setQuizSet((quizSet + 1) % totalSets);
    resetQuiz();
  };

  const calculateProgressPercentage = () => {
    if (!studyMaterials || !progress) return 0;
    
    const flashcardProgress = studyMaterials.flashcards?.length > 0 
      ? (progress.flashcards_mastered?.length || 0) / studyMaterials.flashcards.length * 50 
      : 0;
    const quizProgress = progress.best_quiz_score ? progress.best_quiz_score / 2 : 0;
    
    return Math.round(flashcardProgress + quizProgress);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading study materials...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Document not found</p>
          <Button onClick={() => navigate('/study-builder')} className="mt-4 rounded-xl">
            Back to Study Builder
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/study-builder')}
                variant="ghost"
                size="sm"
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                AI Study Builder
              </Button>
              <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-gray-800 truncate max-w-md" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {document.title}
                </h1>
                <p className="text-sm text-gray-500">
                  by {document.seller_name} • {document.field}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {studyMaterials && studyMaterials.status === 'completed' && (
                <Button
                  data-testid="add-to-course-btn"
                  onClick={() => setShowCourseModal(true)}
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <GraduationCap className="w-4 h-4 mr-1.5" />
                  Add to Course
                </Button>
              )}
              {purchase && (
                <div className="text-sm text-gray-500">
                  Purchased {new Date(purchase.purchase_date).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile title */}
      <div className="sm:hidden px-4 py-3 bg-white border-b border-gray-100">
        <h1 className="text-lg font-semibold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
          {document.title}
        </h1>
        <p className="text-sm text-gray-500">by {document.seller_name}</p>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Document Viewer */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-800">Document</span>
              </div>
            </div>
            <div className={docxHtml ? 'max-h-[600px] overflow-y-auto' : 'h-[250px] sm:h-[500px] lg:h-[600px]'}>
              {docxHtml ? (
                <div
                  className="prose max-w-none p-6"
                  dangerouslySetInnerHTML={{ __html: docxHtml }}
                />
              ) : fileUrl ? (
                <iframe
                  src={fileUrl}
                  className="w-full h-full"
                  title="Document Viewer"
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-gray-50">
                  <div className="text-center p-6">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Document preview not available</p>
                    <Link to={`/document/${documentId}`}>
                      <Button variant="outline" className="mt-4 rounded-xl">
                        View Document Page
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Study Tools */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Tabs */}
            <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50">
              {[
                { id: 'summary', label: 'Summary', icon: BookOpen },
                { id: 'terms', label: 'Key Terms', icon: ListChecks },
                { id: 'flashcards', label: 'Flashcards', icon: Brain },
                { id: 'quiz', label: 'Quiz', icon: HelpCircle },
                { id: 'exam', label: 'Exam Mode', icon: GraduationCap },
                { id: 'progress', label: 'Progress', icon: BarChart3 },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-[350px] sm:h-[450px] lg:h-[550px] overflow-y-auto">
              {generating ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-6">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Generating Study Materials</h3>
                    <p className="text-gray-500">Our AI is analyzing your document...</p>
                    <p className="text-sm text-gray-400 mt-2">This may take a minute</p>
                  </div>
                </div>
              ) : !studyMaterials ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-6">
                    <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Study materials not available yet</p>
                    <Button 
                      onClick={fetchStudyMaterials} 
                      className="mt-4 rounded-xl"
                    >
                      Generate Study Materials
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Summary Tab */}
                  {activeTab === 'summary' && (
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
                        Document Summary
                      </h3>
                      <div className="prose prose-blue max-w-none">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {studyMaterials.summary}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Key Terms Tab */}
                  {activeTab === 'terms' && (
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
                        Key Terms ({studyMaterials.key_terms?.length || 0})
                      </h3>
                      <div className="space-y-3">
                        {studyMaterials.key_terms?.map((item, index) => (
                          <div key={index} className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                            <dt className="font-semibold text-blue-800 mb-1">{item.term}</dt>
                            <dd className="text-gray-700 text-sm">{item.definition}</dd>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Flashcards Tab */}
                  {activeTab === 'flashcards' && studyMaterials.flashcards && (
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                          Flashcards
                        </h3>
                        <span className="text-sm text-gray-500">
                          {currentFlashcard + 1} / {studyMaterials.flashcards.length}
                        </span>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="w-full h-2 bg-gray-200 rounded-full mb-6">
                        <div 
                          className="h-full bg-blue-600 rounded-full transition-all"
                          style={{ width: `${((progress?.flashcards_mastered?.length || 0) / studyMaterials.flashcards.length) * 100}%` }}
                        />
                      </div>

                      {/* Flashcard */}
                      <div 
                        onClick={() => setIsFlipped(!isFlipped)}
                        className="relative h-48 sm:h-64 cursor-pointer perspective-1000"
                      >
                        <div className={`absolute inset-0 transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
                          {/* Front */}
                          <div className={`absolute inset-0 backface-hidden rounded-2xl border-2 ${
                            progress?.flashcards_mastered?.includes(currentFlashcard) 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-blue-200 bg-gradient-to-br from-blue-50 to-white'
                          } p-6 flex items-center justify-center`}>
                            <p className="text-lg text-center text-gray-800 font-medium">
                              {studyMaterials.flashcards[currentFlashcard]?.front}
                            </p>
                            {progress?.flashcards_mastered?.includes(currentFlashcard) && (
                              <div className="absolute top-3 right-3">
                                <Check className="w-6 h-6 text-green-600" />
                              </div>
                            )}
                          </div>
                          {/* Back */}
                          <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 flex items-center justify-center">
                            <p className="text-lg text-center text-gray-800">
                              {studyMaterials.flashcards[currentFlashcard]?.back}
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-center text-sm text-gray-500 mt-2">Click card to flip</p>

                      {/* Controls */}
                      <div className="flex items-center justify-between mt-6 gap-2">
                        <Button
                          onClick={() => { setCurrentFlashcard(Math.max(0, currentFlashcard - 1)); setIsFlipped(false); }}
                          disabled={currentFlashcard === 0}
                          variant="outline"
                          size="sm"
                          className="rounded-xl"
                        >
                          <ChevronLeft className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Previous</span>
                        </Button>
                        
                        <Button
                          onClick={() => handleMasterFlashcard(currentFlashcard)}
                          variant={progress?.flashcards_mastered?.includes(currentFlashcard) ? "outline" : "default"}
                          size="sm"
                          className={`rounded-xl ${progress?.flashcards_mastered?.includes(currentFlashcard) ? 'border-green-300 text-green-700' : 'bg-green-600 hover:bg-green-700'}`}
                        >
                          {progress?.flashcards_mastered?.includes(currentFlashcard) ? (
                            <>
                              <Check className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Mastered</span>
                            </>
                          ) : (
                            <>
                              <Target className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Mark Mastered</span>
                            </>
                          )}
                        </Button>
                        
                        <Button
                          onClick={() => { setCurrentFlashcard(Math.min(studyMaterials.flashcards.length - 1, currentFlashcard + 1)); setIsFlipped(false); }}
                          disabled={currentFlashcard === studyMaterials.flashcards.length - 1}
                          variant="outline"
                          size="sm"
                          className="rounded-xl"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <ChevronRight className="w-4 h-4 sm:ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Quiz Tab */}
                  {activeTab === 'quiz' && studyMaterials.quiz_questions && (() => {
                    const totalSets = studyMaterials.quiz_sets || 1;
                    const currentSetQuestions = getQuizQuestionsForSet();
                    return (
                    <div className="p-6">
                      {/* Quiz Set Navigation (only for multi-set) */}
                      {totalSets > 1 && !quizStarted && !quizResults && (
                        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100" data-testid="quiz-set-nav">
                          <span className="text-sm font-medium text-gray-500 mr-1">Quiz Set:</span>
                          {Array.from({ length: totalSets }, (_, i) => (
                            <button
                              key={i}
                              onClick={() => { setQuizSet(i); resetQuiz(); }}
                              data-testid={`quiz-set-btn-${i}`}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                quizSet === i
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {i + 1}
                            </button>
                          ))}
                        </div>
                      )}

                      {!quizStarted && !quizResults ? (
                        <div className="text-center py-8">
                          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <HelpCircle className="w-10 h-10 text-blue-600" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                            {totalSets > 1 ? `Quiz ${quizSet + 1} of ${totalSets}` : 'Ready to Test Your Knowledge?'}
                          </h3>
                          <p className="text-gray-500 mb-2">{currentSetQuestions.length} questions</p>
                          {totalSets > 1 && (
                            <p className="text-xs text-gray-400 mb-2">Each quiz covers different material from your merged notes</p>
                          )}
                          {progress?.quiz_attempts > 0 && (
                            <p className="text-sm text-gray-500 mb-4">
                              Best score: {progress.best_quiz_score?.toFixed(0)}% ({progress.quiz_attempts} attempts)
                            </p>
                          )}
                          <Button
                            data-testid="start-quiz-btn"
                            onClick={() => { setQuizStarted(true); setSelectedAnswers(new Array(currentSetQuestions.length).fill(-1)); }}
                            className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-800"
                          >
                            Start Quiz {totalSets > 1 ? `${quizSet + 1}` : ''}
                          </Button>
                        </div>
                      ) : quizResults ? (
                        <div className="text-center py-6">
                          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${
                            quizResults.score >= 70 ? 'bg-green-100' : quizResults.score >= 50 ? 'bg-yellow-100' : 'bg-red-100'
                          }`}>
                            <span className={`text-3xl font-bold ${
                              quizResults.score >= 70 ? 'text-green-600' : quizResults.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {quizResults.score.toFixed(0)}%
                            </span>
                          </div>
                          <h3 className="text-xl font-semibold text-gray-800 mb-2">
                            {quizResults.score >= 70 ? 'Great job!' : quizResults.score >= 50 ? 'Good effort!' : 'Keep studying!'}
                          </h3>
                          <p className="text-gray-500 mb-4">
                            You got {quizResults.correct} out of {quizResults.total} correct
                            {totalSets > 1 && ` (Quiz ${quizSet + 1})`}
                          </p>
                          
                          {/* Show results */}
                          <div className="text-left space-y-4 mt-6 max-h-64 overflow-y-auto">
                            {quizResults.results.map((result, index) => (
                              <div key={index} className={`p-4 rounded-xl ${result.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                <p className="font-medium text-gray-800 mb-2">
                                  {index + 1}. {currentSetQuestions[index]?.question}
                                </p>
                                <p className={`text-sm ${result.is_correct ? 'text-green-700' : 'text-red-700'}`}>
                                  {result.is_correct ? '✓ Correct' : `✗ Wrong - Correct answer: ${currentSetQuestions[index]?.options?.[result.correct_answer]}`}
                                </p>
                                {!result.is_correct && result.explanation && (
                                  <p className="text-sm text-gray-600 mt-1">{result.explanation}</p>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex items-center justify-center gap-3 mt-6">
                            <Button onClick={resetQuiz} className="rounded-xl" variant="outline">
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Retry Quiz {totalSets > 1 ? `${quizSet + 1}` : ''}
                            </Button>
                            {totalSets > 1 && (
                              <Button onClick={nextQuizSet} className="rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="next-quiz-set-btn">
                                <ChevronRight className="w-4 h-4 mr-1" />
                                Next Quiz ({quizSet + 1 < totalSets ? quizSet + 2 : 1} of {totalSets})
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-sm text-gray-500">
                              Question {currentQuestion + 1} of {currentSetQuestions.length}
                              {totalSets > 1 && ` · Quiz ${quizSet + 1}`}
                            </span>
                            <div className="w-32 h-2 bg-gray-200 rounded-full">
                              <div 
                                className="h-full bg-blue-600 rounded-full transition-all"
                                style={{ width: `${((currentQuestion + 1) / currentSetQuestions.length) * 100}%` }}
                              />
                            </div>
                          </div>

                          <h3 className="text-lg font-medium text-gray-800 mb-4">
                            {currentSetQuestions[currentQuestion]?.question}
                          </h3>

                          <div className="space-y-2">
                            {currentSetQuestions[currentQuestion]?.options?.map((option, index) => (
                              <button
                                key={index}
                                onClick={() => {
                                  const newAnswers = [...selectedAnswers];
                                  newAnswers[currentQuestion] = index;
                                  setSelectedAnswers(newAnswers);
                                }}
                                className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                                  selectedAnswers[currentQuestion] === index
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <span className="font-medium text-gray-600 mr-2">
                                  {String.fromCharCode(65 + index)}.
                                </span>
                                {option}
                              </button>
                            ))}
                          </div>

                          <div className="flex items-center justify-between mt-6 gap-2">
                            <Button
                              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                              disabled={currentQuestion === 0}
                              variant="outline"
                              size="sm"
                              className="rounded-xl"
                            >
                              <ChevronLeft className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Previous</span>
                            </Button>
                            
                            {currentQuestion === currentSetQuestions.length - 1 ? (
                              <Button
                                onClick={handleSubmitQuiz}
                                disabled={submittingQuiz || selectedAnswers.includes(-1)}
                                className="rounded-xl bg-green-600 hover:bg-green-700"
                                data-testid="submit-quiz-btn"
                              >
                                {submittingQuiz ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Submitting...
                                  </>
                                ) : (
                                  'Submit Quiz'
                                )}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                                size="sm"
                                className="rounded-xl"
                              >
                                <span className="hidden sm:inline">Next</span>
                                <ChevronRight className="w-4 h-4 sm:ml-1" />
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })()}

                  {/* Exam Mode Tab */}
                  {activeTab === 'exam' && (
                    <ExamMode documentId={documentId} user={user} />
                  )}

                  {/* Progress Tab */}
                  {activeTab === 'progress' && (
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
                        Your Progress
                      </h3>

                      {/* Overall Progress */}
                      <div className="mb-8">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                          <span className="text-2xl font-bold text-blue-600">{calculateProgressPercentage()}%</span>
                        </div>
                        <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${calculateProgressPercentage()}%` }}
                          />
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                          <div className="flex items-center gap-3 mb-2">
                            <Brain className="w-5 h-5 text-blue-600" />
                            <span className="text-sm text-gray-600">Flashcards</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-800">
                            {progress?.flashcards_mastered?.length || 0} / {studyMaterials?.flashcards?.length || 0}
                          </p>
                          <p className="text-xs text-gray-500">Mastered</p>
                        </div>

                        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                          <div className="flex items-center gap-3 mb-2">
                            <Trophy className="w-5 h-5 text-green-600" />
                            <span className="text-sm text-gray-600">Best Score</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-800">
                            {progress?.best_quiz_score?.toFixed(0) || 0}%
                          </p>
                          <p className="text-xs text-gray-500">Quiz</p>
                        </div>

                        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                          <div className="flex items-center gap-3 mb-2">
                            <HelpCircle className="w-5 h-5 text-amber-600" />
                            <span className="text-sm text-gray-600">Quiz Attempts</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-800">
                            {progress?.quiz_attempts || 0}
                          </p>
                          <p className="text-xs text-gray-500">Total</p>
                        </div>

                        <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                          <div className="flex items-center gap-3 mb-2">
                            <Clock className="w-5 h-5 text-purple-600" />
                            <span className="text-sm text-gray-600">Last Studied</span>
                          </div>
                          <p className="text-lg font-bold text-gray-800">
                            {progress?.last_studied 
                              ? new Date(progress.last_studied).toLocaleDateString()
                              : 'Never'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add to Course Modal */}
      {showCourseModal && (
        <AddToCourseModal
          documentId={documentId}
          documentTitle={document?.title || ''}
          onClose={() => setShowCourseModal(false)}
          onSuccess={() => toast.success('Document organized into course!')}
        />
      )}

      {/* CSS for 3D flip effect */}
      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .transform-style-preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}</style>
    </div>
  );
};

export default StudyDashboard;
